/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { Button } from '@/components/buttons';
import { Typography } from '@/components/typography';
import ReduceShippingMethods from './reduce-shipping-methods';
import { useBasket } from '@/providers/basket';
import { getDefaultShippingMethod } from '@/lib/customer/profile-utils';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import type { CheckoutActionData } from '../types';
import type { ShopperBasketsV2 } from '@/scapi';
import CheckoutErrorBanner from './checkout-error-banner';
import { getCheckoutDisplayError } from '../utils/checkout-display-error';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/providers/currency';
import { formatCurrency } from '@/lib/currency';
import { ShippingMethodDetailsModal, type ShippingMethodDetailsContentAssets } from './shipping-method-details-modal';

interface ShippingMethod {
    id: string;
    name: string;
    description?: string;
    price: number;
    [key: string]: unknown;
}

interface ShippingOptionsProps {
    onSubmit: (formData: FormData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    shippingMethods?: ShopperBasketsV2.schemas['ShippingMethodResult'];
    // Step state managed by container
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
    /**
     * Deferred BM content assets for the shipping-method details modal body, keyed by delivery
     * method. The modal selects the matching asset based on its active `(variant, methodName)`
     * pair and renders its HTML body — no i18n / Page Designer fallback. See
     * `CHECKOUT_DELIVERY_DETAILS_CONTENT_ASSET_IDS` in `./shipping-method-details-modal.tsx`.
     *
     * NOTE: this OOTB picker doesn't currently pass a `(variant, methodName)` pair to the modal,
     * so the body resolves to `undefined` and the modal renders empty. CHECKOUT-10's
     * `<DeliveryGroups/>` is the primary entry point for the per-method modal; the OOTB picker is
     * only mounted in BOPIS-only and multi-address legacy paths.
     */
    shippingMethodDetailsContentAssets?: ShippingMethodDetailsContentAssets;
    validationError?: string | null;
}

export default function ShippingOptions({
    onSubmit,
    isLoading,
    actionData,
    shippingMethods,
    isCompleted: _isCompleted,
    isEditing,
    onEdit,
    shippingMethodDetailsContentAssets,
    validationError: _validationError,
}: ShippingOptionsProps) {
    const cart = useBasket();
    const customerProfile = useCustomerProfile();
    const currency = useCurrency();
    const { i18n } = useTranslation();
    const [detailsOpen, setDetailsOpen] = useState(false);

    const availableShippingMethods: ShippingMethod[] = useMemo(
        () =>
            shippingMethods?.applicableShippingMethods
                ?.filter((method): method is NonNullable<typeof method> & { id: string; name: string; price: number } =>
                    Boolean(method.id && method.name && typeof method.price === 'number' && !Number.isNaN(method.price))
                )
                .map((method) => ({
                    id: method.id,
                    name: method.name,
                    description: method.description,
                    price: method.price,
                })) || [],
        [shippingMethods?.applicableShippingMethods]
    );
    const { t } = useTranslation('checkout');

    const selectedMethod = cart?.shipments?.[0]?.shippingMethod;
    // Basket may only have shippingMethod.id after prefill; resolve name/price from applicable methods for summary
    const summaryMethod: ShippingMethod | undefined = useMemo(() => {
        if (!selectedMethod?.id) return undefined;
        const fromList = availableShippingMethods.find((m) => m.id === selectedMethod.id);
        if (fromList) return fromList;
        return {
            id: selectedMethod.id,
            name: selectedMethod.name ?? selectedMethod.id,
            description: selectedMethod.description,
            price: typeof selectedMethod.price === 'number' ? selectedMethod.price : 0,
        };
    }, [selectedMethod, availableShippingMethods]);
    const isGuest = !customerProfile?.customer?.customerId;
    const hideChangeForGuest = isGuest && !selectedMethod;
    const isUpcomingStep = !isEditing && !selectedMethod;

    const defaultShippingMethodId = getDefaultShippingMethod(
        availableShippingMethods,
        selectedMethod,
        shippingMethods?.defaultShippingMethodId
    );
    const shippingOptionsError = getCheckoutDisplayError(actionData, 'shippingOptions');

    // Track if we've already auto-submitted to prevent infinite loops
    const hasAutoSubmitted = useRef(false);

    // Auto-apply default shipping method for returning customers only
    // Guest users should always see and choose shipping options manually
    useEffect(() => {
        if (
            isEditing &&
            !selectedMethod?.id &&
            customerProfile &&
            availableShippingMethods.length > 0 &&
            !hasAutoSubmitted.current &&
            !isLoading
        ) {
            hasAutoSubmitted.current = true;

            const isDefaultValid =
                defaultShippingMethodId &&
                availableShippingMethods.some((method) => method.id === defaultShippingMethodId);
            const methodIdToSubmit = isDefaultValid ? defaultShippingMethodId : availableShippingMethods[0]?.id;

            if (methodIdToSubmit) {
                const formData = new FormData();
                formData.append('shippingMethodId', methodIdToSubmit);
                onSubmit(formData);
            }
        }

        // Reset auto-submit flag when user moves away from this step
        if (!isEditing) {
            hasAutoSubmitted.current = false;
        }
    }, [
        isEditing,
        selectedMethod?.id,
        shippingMethods?.applicableShippingMethods,
        defaultShippingMethodId,
        onSubmit,
        isLoading,
        customerProfile,
        availableShippingMethods,
    ]);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        onSubmit(formData);
    };

    // For single page layout, always show the component but in collapsed state when not editing
    // The ToggleCard will handle the collapsed/expanded state based on editing prop

    const stepTitle = (
        <span className="text-2xl font-bold leading-8 tracking-tight text-card-foreground">
            {t('shippingOptions.title')}
        </span>
    );

    return (
        <ToggleCard
            id="shipping-options"
            title={stepTitle}
            editing={isEditing}
            disabled={false}
            onEdit={onEdit}
            editLabel={t('common.edit')}
            disableEdit={hideChangeForGuest || isUpcomingStep}
            showHeaderSeparator
            isLoading={isLoading}>
            <ToggleCardEdit>
                {/* Body vertical rhythm: header→content uses ToggleCard gap-4 (16px); no extra pt (Figma Body container 25706:53930). */}
                <form method="post" className="flex flex-col gap-4" onSubmit={handleSubmit}>
                    {shippingOptionsError && <CheckoutErrorBanner message={shippingOptionsError} />}

                    <div className="flex flex-col gap-4">
                        {availableShippingMethods.length > 0 ? (
                            <ReduceShippingMethods
                                methods={availableShippingMethods}
                                name="shippingMethodId"
                                defaultValue={selectedMethod?.id || defaultShippingMethodId || ''}
                                onShowDetails={() => setDetailsOpen(true)}
                                data-testid="shipping-options-methods"
                            />
                        ) : (
                            <div className="flex justify-center rounded-lg border-2 border-dashed border-muted p-8">
                                <div className="space-y-2 text-center">
                                    <Typography variant="p" className="text-muted-foreground">
                                        {t('shippingOptions.noMethodsAvailable')}
                                    </Typography>
                                    <Typography variant="small" className="text-muted-foreground">
                                        {t('shippingOptions.noMethodsAvailableHelp')}
                                    </Typography>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pt-2">
                        <Button
                            type="submit"
                            disabled={isLoading || availableShippingMethods.length === 0}
                            className="h-auto whitespace-normal w-full lg:w-fit">
                            {isLoading ? t('shippingOptions.saving') : t('shippingOptions.continue')}
                        </Button>
                    </div>
                </form>
            </ToggleCardEdit>

            <ShippingMethodDetailsModal
                open={detailsOpen}
                onOpenChange={setDetailsOpen}
                contentAssets={shippingMethodDetailsContentAssets}
            />

            <ToggleCardSummary>
                {summaryMethod ? (
                    <div className="space-y-1.5">
                        <div className="space-y-1.5">
                            {summaryMethod.description && (
                                <p className="text-sm font-normal leading-5 text-foreground">
                                    {t('shippingOptions.arrives', {
                                        estimatedArrivalTime: summaryMethod.description,
                                    })}
                                </p>
                            )}
                            <p className="text-sm font-normal leading-5 text-foreground">
                                {t('shippingOptions.priceAndMethod', {
                                    price:
                                        summaryMethod.price === 0
                                            ? t('shippingOptions.free')
                                            : formatCurrency(summaryMethod.price ?? 0, i18n.language, currency),
                                    methodName: summaryMethod.name || '',
                                })}
                            </p>
                            <button
                                type="button"
                                onClick={() => setDetailsOpen(true)}
                                className="w-fit text-sm text-primary underline underline-offset-2 hover:no-underline">
                                {t('shippingOptions.details')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        {isGuest ? t('shippingOptions.completePreviousSteps') : t('shippingOptions.enterAddressFirst')}
                    </p>
                )}
            </ToggleCardSummary>
        </ToggleCard>
    );
}
